/**
 * The PIN session, kept in localStorage.
 *
 * localStorage rather than memory because a trade-show iPad gets reloaded, rotated and put to
 * sleep, and being thrown back to the PIN screen mid-conversation is the failure that actually
 * costs something. It sits next to the questionnaire draft (B4), which is stored for the same
 * reason.
 *
 * What is stored is a signed token that expires — not the PIN. The PIN itself is never written
 * anywhere on the client.
 */
export type Role = 'SalesRep' | 'Admin';

export interface Session {
  readonly token: string;
  readonly role: Role;
  /** ISO-8601, as returned by the API. */
  readonly expiresAt: string;
}

const KEY = 'sovereignty.session';

/** A small margin, so a token cannot expire between this check and the request it guards. */
const EXPIRY_MARGIN_MS = 30_000;

function isLive(session: Session): boolean {
  const expires = Date.parse(session.expiresAt);
  return Number.isFinite(expires) && expires - EXPIRY_MARGIN_MS > Date.now();
}

export function loadSession(): Session | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    // Private browsing and locked-down kiosk profiles can both refuse storage. Treat it as not
    // signed in rather than crashing the whole app on boot.
    return null;
  }

  if (!raw) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearSession();
    return null;
  }

  const candidate = parsed as Partial<Session> | null;

  if (
    !candidate ||
    typeof candidate.token !== 'string' ||
    typeof candidate.expiresAt !== 'string' ||
    (candidate.role !== 'SalesRep' && candidate.role !== 'Admin')
  ) {
    clearSession();
    return null;
  }

  const session: Session = {
    token: candidate.token,
    role: candidate.role,
    expiresAt: candidate.expiresAt,
  };

  if (!isLive(session)) {
    clearSession();
    return null;
  }

  return session;
}

export function saveSession(session: Session): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    // Not fatal: the token still works for this tab, it just will not survive a reload.
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to do — there was no usable storage to begin with.
  }
}

/** The bearer token, or undefined when there is no live session. */
export function currentToken(): string | undefined {
  return loadSession()?.token;
}
